import React from 'react';
import type {TableColumnsType} from 'antd';
import {Button, Table} from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {PickupTaskStatus, PickupTaskType} from '../types/PickupTaskType';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { usePickupStartPartialGray } from '../../../../hooks/usePickupStartPartialGray';
import { formatEpochSecondsInHubZone } from '../../../../utils/timezoneUtils';
import { usePickupData } from '../contexts/PickupDataContext';
import { formatISODateTime } from 'utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
// 🔧 添加国际化Hook导入
import { usePickupTaskInternationalization } from '../hooks/usePickupTaskInternationalization';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';

dayjs.extend(utc);
dayjs.extend(timezone);

interface PickupTaskTableProps {
    data: PickupTaskType[];
    loading: boolean;
    onCancelTask?: (taskId: string) => void;
    onShowDetail?: (taskId: string) => void;
    onSelectChange: (newSelectedRowKeys: React.Key[], newSelectedRows: PickupTaskType[]) => void;
    selectedRowKeys: React.Key[];
}

const PickupTaskTable: React.FC<PickupTaskTableProps> = ({
    data,
    loading,
    onCancelTask,
    onShowDetail,
    onSelectChange,
    selectedRowKeys,
}) => {
    // 获取仓库ID
    const { warehouseId } = usePickupData();
    const { currentWarehouse } = useWarehouseContext();
    // 灰度：未命中的 hub 隐藏该列。非灰度 hub 的 act_departure_time 仍由惰性写入维护、
    // 语义是「首次点位上报时刻」而非司机打点，列名会误导，故整列不展示
    const { startPartialEnabled } = usePickupStartPartialGray();
    const {t, i18n} = useTranslation();
    const { vehicleTypes } = useVehicleTypes();

    // 🔧 使用国际化Hook
    const { getTaskStatusText } = usePickupTaskInternationalization();

    // 获取状态对应的颜色
    const getStatusColor = (status: PickupTaskStatus): string => {
        switch (status) {
            case PickupTaskStatus.DRAFT:
                return 'default';
            case PickupTaskStatus.PUBLISHED:
                return 'blue';
            case PickupTaskStatus.IN_PROGRESS:
                return 'processing';
            case PickupTaskStatus.COMPLETED:
                return 'success';
            case PickupTaskStatus.CANCELLED:
                return 'error';
            default:
                return 'default';
        }
    };

    /**
     * 实际出发时间按 HUB 时区展示，口径与揽收进度页一致。
     * 不能用后端的 actDepartureTimeAt：列表走的 toPickupTaskResponse() 没传时区，
     * convertUtcToLocalTimeString 对空时区直接返回 null，该字段在列表里恒为空。
     * 本页任务已按 currentWarehouse 过滤，用它的时区不会串仓。
     * 历史时刻按 IANA 时区换算（跨冬令时不差 1 小时），见 formatEpochSecondsInHubZone。
     */
    const formatDepartureTime = (timestamp?: number): string =>
        formatEpochSecondsInHubZone(timestamp, currentWarehouse, '-');

    // 处理取消任务
    const handleCancelTask = (taskId: string) => {
        if (onCancelTask) {
            onCancelTask(taskId);
        }
    };

    const columns: TableColumnsType<PickupTaskType> = [
        {
            title: t('pickup.pickupTask.columns.taskNumber'),
            dataIndex: 'taskNumber',
            key: 'taskNumber',
            width: 180,
            align: 'left' as const,
            ellipsis: true,
            render: (text: string, record: PickupTaskType) => (
                <Button
                    type="link"
                    style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}
                    onClick={() => onShowDetail && onShowDetail(record.id)}
                >
                    {text}
                </Button>
            )
        },
        {
            title: t('pickup.pickupTask.columns.taskDate'),
            dataIndex: 'taskDate',
            key: 'taskDate',
            width: 120,
            align: 'left' as const,
            ellipsis: true,
            render: (date: string) => <span style={{ whiteSpace: 'nowrap' }}>{dayjs(date).format('YYYY-MM-DD')}</span>
        },
        {
            title: t('pickup.pickupTask.columns.route'),
            key: 'route',
            width: 160,
            align: 'left' as const,
            ellipsis: true,
            render: (_: any, record: PickupTaskType) => {
              return <span style={{ whiteSpace: 'nowrap' }}>{record.routeName || '-'}</span>;
            }
        },
        {
            title: t('pickup.pickupTask.columns.taskStatus'),
            dataIndex: 'status',
            key: 'status',
            width: 130,
            align: 'left' as const,
            ellipsis: true,
            render: (status: PickupTaskStatus) => <span style={{ whiteSpace: 'nowrap' }}>{getTaskStatusText(status)}</span>
        },
        {
            title: t('pickup.pickupTask.columns.driver'),
            dataIndex: 'driverName',
            key: 'driverName',
            width: 160,
            align: 'left' as const,
            ellipsis: true,
            render: (driverName: string) => <span style={{ whiteSpace: 'nowrap' }}>{driverName || '-'}</span>
        },
        {
            title: t('pickup.pickupTask.columns.vehicle'),
            dataIndex: 'vehicleLicensePlate',
            key: 'vehicleLicensePlate',
            width: 120,
            align: 'left' as const,
            ellipsis: true,
            render: (number: string) => <span style={{ whiteSpace: 'nowrap' }}>{number || '-'}</span>
        },
        {
            title: t('pickup.pickupTask.columns.vehicleModel'),
            dataIndex: 'vehicleTypeId',
            key: 'vehicleTypeId',
            width: 140,
            align: 'left' as const,
            ellipsis: true,
            render: (vehicleTypeId: number) => {
                const vt = vehicleTypes?.find(v => String(v.id) === String(vehicleTypeId));
                const name = vt ? vt.nameEn : undefined;
                return <span style={{ whiteSpace: 'nowrap' }}>{name || '-'}</span>;
            }
        },
        {
            title: t('pickup.pickupTask.columns.origin'),
            dataIndex: 'startPointName',
            key: 'startPointName',
            width: 160,
            align: 'left' as const,
            ellipsis: true,
            render: (origin: string) => <span style={{ whiteSpace: 'nowrap' }}>{origin || '-'}</span>
        },
        {
            title: t('pickup.pickupTask.columns.destination'),
            dataIndex: 'endPointName',
            key: 'endPointName',
            width: 160,
            align: 'left' as const,
            ellipsis: true,
            render: (dest: string) => <span style={{ whiteSpace: 'nowrap' }}>{dest || '-'}</span>
        },
        {
            title: t('pickup.pickupTask.columns.appointments'),
            dataIndex: 'appointmentCount',
            key: 'appointmentCount',
            width: 100,
            align: 'left' as const,
            ellipsis: true,
            render: (count: number) => {
                const validCount = count || 0;
                return <span style={{ whiteSpace: 'nowrap' }}>{validCount}</span>;
            }
        },
        {
            title: t('pickup.pickupTask.columns.loadRate'),
            dataIndex: 'loadRate',
            key: 'loadRate',
            width: 100,
            align: 'left' as const,
            ellipsis: true,
            render: (rate: string) => <span style={{ whiteSpace: 'nowrap' }}>{rate || '-'}</span>
        },
        ...(startPartialEnabled(currentWarehouse?.id) ? [{
            // 司机滑动「开始作业」打的点，即司机实际开始揽收的时刻
            title: t('pickup.pickupTask.columns.actDepartureTime'),
            dataIndex: 'actDepartureTime',
            key: 'actDepartureTime',
            width: 180,
            align: 'left' as const,
            ellipsis: true,
            render: (actDepartureTime: number) => (
                <span style={{ whiteSpace: 'nowrap' }}>{formatDepartureTime(actDepartureTime)}</span>
            )
        }] : []),
        {
            title: t('pickup.appointment.columns.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            align: 'left' as const,
            ellipsis: true,
            render: (updatedAt: string) => (
                <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>{updatedAt ? formatISODateTime(updatedAt) : '--'}</span>
            )
        },
        {
            title: t('pickup.pickupTask.columns.action'),
            key: 'action',
            width: 120,
            align: 'left' as const,
            fixed: 'right' as const,
            ellipsis: true,
            render: (_, record: PickupTaskType) => {
                // 判断是否允许取消
                const canCancel = record.status === PickupTaskStatus.DRAFT || record.status === PickupTaskStatus.PUBLISHED;
                const canOperatePickupTask = hasResource([ResourceCode.menu.PICKUP_TASK_OPERATE]);

                return (
                    <div className="space-x-2">
                        <Button
                            type="link"
                            size="small"
                            danger
                            disabled={!canCancel}
                            hidden={!canOperatePickupTask}
                            onClick={() => canCancel && handleCancelTask(record.id)}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {t('pickup.pickupTask.columns.cancelTask')}
                        </Button>
                    </div>
                );
            }
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: PickupTaskType) => ({
            disabled: record.status !== 'DRAFT',
        }),
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
        ],
        columnWidth: 40,
    };

    return (
        <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1200 }}
            pagination={false}
            size="middle"
            className="pickup-task-table mb-4"
            tableLayout="fixed"
        />
    );
};

export default PickupTaskTable; 